import { z } from "zod";

const u16 = z
	.array(z.number().int().min(0).max(65535))
	.transform((v): Uint16Array => new Uint16Array(v));
const u8 = z
	.array(z.number().int().min(0).max(255))
	.transform((v): Uint8Array => new Uint8Array(v));
const bounds = z.object({
	minX: z.number(),
	minY: z.number(),
	maxX: z.number(),
	maxY: z.number(),
});
const compactSchema = z
	.object({
		quantization: z.object({
			minX: z.number(),
			minY: z.number(),
			scaleX: z.number().positive(),
			scaleY: z.number().positive(),
		}),
		segmentStride: z.literal(9),
		segments: u16,
		pathSegmentOffsets: z
			.array(z.number().int().nonnegative())
			.length(2)
			.transform((v): Uint32Array => new Uint32Array(v)),
		styleCount: z.number().int().nonnegative().max(65536),
		styleColorsRgba: u8,
		styleLineWidths: u16,
		styleDashPatterns: u16,
		styleDashPhases: u16,
		styleJoinCaps: u8,
		styleMiterLimits: u16,
		joinEncoding: z.union([z.literal(0), z.literal(1)]),
	})
	.superRefine((c, ctx) => {
		if (
			c.segments.length % 9 ||
			c.pathSegmentOffsets[0] !== 0 ||
			c.pathSegmentOffsets[1] !== c.segments.length / 9 ||
			c.styleColorsRgba.length !== c.styleCount * 4 ||
			c.styleLineWidths.length !== c.styleCount ||
			c.styleDashPatterns.length !== c.styleCount * 4 ||
			c.styleDashPhases.length !== c.styleCount ||
			c.styleJoinCaps.length !== c.styleCount * 2 ||
			c.styleMiterLimits.length !== c.styleCount
		) {
			ctx.addIssue({
				code: "custom",
				message: "Invalid compact snapshot dimensions",
			});
		}
		for (let i = 5; i < c.segments.length; i += 9) {
			if ((c.segments[i] ?? 0) >= c.styleCount) {
				ctx.addIssue({
					code: "custom",
					message: "Invalid compact snapshot style",
				});
			}
		}
	});

const renderSourceSchema = z.object({
	order: z.number().nonnegative(),
	compact: compactSchema.optional(),
	clip: z
		.object({
			id: z.literal(1),
			bbox: bounds,
			pieces: z.array(
				z.object({
					points: z
						.array(z.number())
						.min(6)
						.refine((v) => v.length % 2 === 0)
						.transform((v): Float32Array => new Float32Array(v)),
				}),
			),
		})
		.optional(),
});

/** Self-contained source data. IDs are local; no reference to another revision's atlas. */
export type GeometryRenderSource = z.output<typeof renderSourceSchema>;
export type CompactRenderSource = Omit<
	NonNullable<GeometryRenderSource["compact"]>,
	"segmentStride" | "joinEncoding"
> & { segmentStride: number; joinEncoding: number };

export const encodeGeometryRenderSource = (
	source: GeometryRenderSource,
): Uint8Array =>
	new TextEncoder().encode(
		JSON.stringify(source, (_key, value: unknown) =>
			value instanceof Uint8Array ||
			value instanceof Uint16Array ||
			value instanceof Uint32Array ||
			value instanceof Float32Array
				? Array.from(value)
				: value,
		),
	);

export const decodeGeometryRenderSource = (
	bytes: Uint8Array,
): GeometryRenderSource =>
	renderSourceSchema.parse(
		JSON.parse(new TextDecoder().decode(bytes)) as unknown,
	);

/** Copy one path and only its referenced styles; never retain the entire page buffer. */
export const captureGeometryRenderSource = ({
	compact,
	pathIndex,
	order,
	clip,
}: {
	compact?: CompactRenderSource | null;
	pathIndex: number;
	order: number;
	clip?: {
		bbox: z.output<typeof bounds>;
		pieces: readonly { points: Float32Array }[];
	};
}): GeometryRenderSource => {
	const source: GeometryRenderSource = { order };
	if (clip) {
		source.clip = {
			id: 1,
			bbox: { ...clip.bbox },
			pieces: clip.pieces.map(({ points }) => ({ points: points.slice() })),
		};
	}
	if (!compact) {
		return source;
	}
	if (
		compact.segmentStride !== 9 ||
		(compact.joinEncoding !== 0 && compact.joinEncoding !== 1)
	) {
		throw new Error("Unsupported compact stroke encoding");
	}
	const start = compact.pathSegmentOffsets[pathIndex * 2] ?? 0;
	const count = compact.pathSegmentOffsets[pathIndex * 2 + 1] ?? 0;
	const segments = compact.segments.slice(start * 9, (start + count) * 9);
	const styles = new Map<number, number>();
	for (let i = 5; i < segments.length; i += 9) {
		const old = segments[i] ?? 0;
		if (!styles.has(old)) {
			styles.set(old, styles.size);
		}
		segments[i] = styles.get(old) ?? 0;
	}
	const copy = (values: Uint8Array | Uint16Array, stride: number): number[] =>
		[...styles.keys()].flatMap((id) =>
			Array.from(values.subarray(id * stride, (id + 1) * stride)),
		);
	source.compact = {
		quantization: { ...compact.quantization },
		segmentStride: 9,
		segments,
		pathSegmentOffsets: new Uint32Array([0, count]),
		styleCount: styles.size,
		styleColorsRgba: new Uint8Array(copy(compact.styleColorsRgba, 4)),
		styleLineWidths: new Uint16Array(copy(compact.styleLineWidths, 1)),
		styleDashPatterns: new Uint16Array(copy(compact.styleDashPatterns, 4)),
		styleDashPhases: new Uint16Array(copy(compact.styleDashPhases, 1)),
		styleJoinCaps: new Uint8Array(copy(compact.styleJoinCaps, 2)),
		styleMiterLimits: new Uint16Array(copy(compact.styleMiterLimits, 1)),
		joinEncoding: compact.joinEncoding,
	};
	return source;
};
